import { useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from '@lightning/analytics';
import { useAuth } from '@/contexts/AuthContext';

function getPostHogSessionId(posthog: ReturnType<typeof usePostHog>): string | undefined {
  return (posthog as unknown as { get_session_id?: () => string | undefined })?.get_session_id?.();
}

function getPostHogDistinctId(posthog: ReturnType<typeof usePostHog>): string | undefined {
  return (posthog as unknown as { get_distinct_id?: () => string | undefined })?.get_distinct_id?.();
}

function withSessionId(
  properties: Record<string, unknown>,
  posthog: ReturnType<typeof usePostHog>,
): Record<string, unknown> {
  if (properties.session_id != null) return properties;
  return {
    ...properties,
    session_id: getPostHogSessionId(posthog),
  };
}

/**
 * Convenience hook for tracking Howie analytics events.
 * Automatically provides PostHog instance, user ID, and (when anonymous)
 * the PostHog distinct_id as the dbAnonymousId so server-side joins work.
 *
 * app:'howie' reaches the server payload via @lightning/analytics:
 * AnalyticsProvider calls initAnalytics({app:'howie',...}) which sets the
 * module's shared props; core.ts trackEvent merges them into every payload
 * (packages/analytics/src/core.ts — initAnalytics + mergedProperties).
 */
export function useHowieAnalytics() {
  const posthog = usePostHog();
  const { user } = useAuth();

  const track = useCallback(
    (eventName: string, properties: Record<string, unknown> = {}) => {
      const dbAnonymousId = user?.id ? null : getPostHogDistinctId(posthog);
      // Fire-and-forget: trackEvent is async (cannot throw synchronously)
      // and handles its own errors; void marks the unawaited promise as
      // intentional. Mirrors useYochaiAnalytics.
      void trackEvent(
        eventName,
        withSessionId(properties, posthog),
        posthog,
        user?.id,
        dbAnonymousId,
      );
    },
    [posthog, user?.id]
  );

  return { track, posthog };
}
